import React, { lazy, Suspense, useEffect, useState } from 'react';
import type { BrandFilterViewModel } from 'autogen/swagger/all';
import { isSmartviewOpen } from 'components/generic/smartview/shared/recoil/selectors';
import Flex from 'components/Layout/components/Flex';
import Loader from 'components/Loader';
import { useRecoilValue } from 'recoil';
import FilterMenuSmartView from '../../FilterMenu/FilterMenuSmartView';
import useFilterMenu from '../../FilterMenu/shared/hooks/useFilterMenu';

const LazyBrandFilterMenu = lazy(() => import('./BrandFilterMenu'));

// Fallback shell shown while lazy chunk loads; ensures correct header is displayed right away
const BrandFilterMenuFallback = ({ model }: { model: BrandFilterViewModel }) => {
    const id = model.attribute.id.toString();
    const { closeFilterMenu } = useFilterMenu(id);
    return (
        <FilterMenuSmartView
            name={id}
            headline={model.displayName}
            backButtonCallback={closeFilterMenu}
            withSearch={model.showSearch}
        >
            <Flex justifyContent={'center'}>
                <Loader variant={'default'} />
            </Flex>
        </FilterMenuSmartView>
    );
};

// Gate component
export default function BrandFilterMenuLazy({ model }: { model: BrandFilterViewModel }) {
    const id = model.attribute.id.toString();
    const isOpen = useRecoilValue(isSmartviewOpen(id));
    const [hasOpened, setHasOpened] = useState(false);

    // Set true when the menu is opened for the first time
    useEffect(() => {
        if (isOpen && !hasOpened) {
            setHasOpened(true);
        }
    }, [isOpen, hasOpened]);

    if (!hasOpened) return null;

    return (
        <Suspense fallback={<BrandFilterMenuFallback model={model} />}>
            <LazyBrandFilterMenu model={model} />
        </Suspense>
    );
}
